import { useContext, useState } from "react";
import { Archive, ArchiveRestore, ChevronRight, Pencil, Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import LeadTagDialog from "./LeadTagDialog";
import TagChip from "./TagChip";

type LeadTag = RouterOutputs["leadTags"]["list"][number];

const LeadTagsManager = () => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const tags = trpc.leadTags.list.useQuery({ merchantId }, { enabled: !!merchantId });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<LeadTag | null>(null);
  const [tagToArchive, setTagToArchive] = useState<LeadTag | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const invalidate = () => utils.leadTags.list.invalidate({ merchantId });
  const handleError = (fallback: string) => (error: { message?: string }) =>
    toast.error(error.message || fallback);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTag(null);
  };

  const createTag = trpc.leadTags.create.useMutation({
    onSuccess: () => {
      invalidate();
      handleCloseDialog();
      toast.success("Etiqueta creada");
    },
    onError: handleError("No se pudo crear la etiqueta"),
  });

  const updateTag = trpc.leadTags.update.useMutation({
    onSuccess: () => {
      invalidate();
      handleCloseDialog();
      toast.success("Etiqueta actualizada");
    },
    onError: handleError("No se pudo actualizar la etiqueta"),
  });

  const setArchived = trpc.leadTags.setArchived.useMutation({
    onSuccess: (_, { archived }) => {
      invalidate();
      setTagToArchive(null);
      toast.success(archived ? "Etiqueta archivada" : "Etiqueta restaurada");
    },
    onError: handleError("No se pudo actualizar la etiqueta"),
  });

  const handleOpenCreate = () => {
    setEditingTag(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (tag: LeadTag) => {
    setEditingTag(tag);
    setIsDialogOpen(true);
  };

  const handleSubmit = (data: { name: string; color: LeadTag["color"] }) => {
    if (editingTag) {
      updateTag.mutate({ merchantId, id: editingTag.id, ...data });
      return;
    }
    createTag.mutate({ merchantId, ...data });
  };

  if (tags.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const tagList = tags.data ?? [];
  const activeTags = tagList.filter((tag) => tag.status === "active");
  const archivedTags = tagList.filter((tag) => tag.status === "archived");

  return (
    <div>
      <h2 className="mb-4 text-xl font-bold">Etiquetas</h2>
      <p className="mb-6 text-sm text-gray-600">
        Etiquetas para identificar y filtrar leads en el Tablero y la Bandeja.
      </p>
      <div className="flex flex-col gap-4">
        <div>
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 size-4" />
            Nueva etiqueta
          </Button>
        </div>

        {tagList.length === 0 ? (
          <Card className="flex flex-col items-center gap-3 border-gray-200 p-8 text-center">
            <Tag className="size-8 text-muted-foreground" aria-hidden />
            <p className="max-w-md text-sm text-muted-foreground">
              Aún no hay etiquetas. Crea la primera para empezar a clasificar tus leads.
            </p>
          </Card>
        ) : null}

        {activeTags.length > 0 ? (
          <Card className="divide-y divide-gray-200 border-gray-200">
            {activeTags.map((tag) => (
              <div key={tag.id} className="flex items-center justify-between gap-2 px-4 py-1">
                <TagChip name={tag.name} color={tag.color} />
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="min-h-11 min-w-11"
                    aria-label={`Editar ${tag.name}`}
                    onClick={() => handleOpenEdit(tag)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="min-h-11 min-w-11"
                    aria-label={`Archivar ${tag.name}`}
                    onClick={() => setTagToArchive(tag)}
                  >
                    <Archive className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        ) : null}

        {archivedTags.length > 0 ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              aria-expanded={showArchived}
              onClick={() => setShowArchived(!showArchived)}
              className="flex min-h-11 items-center gap-1 self-start text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              <ChevronRight
                className={cn("size-4 transition-transform", showArchived && "rotate-90")}
              />
              Archivadas ({archivedTags.length})
            </button>
            {showArchived ? (
              <Card className="divide-y divide-gray-200 border-gray-200">
                {archivedTags.map((tag) => (
                  <div key={tag.id} className="flex items-center justify-between gap-2 px-4 py-1">
                    <TagChip name={tag.name} color={tag.color} />
                    <Button
                      variant="ghost"
                      className="min-h-11 shrink-0"
                      disabled={setArchived.isLoading}
                      onClick={() =>
                        setArchived.mutate({ merchantId, id: tag.id, archived: false })
                      }
                    >
                      <ArchiveRestore className="mr-2 size-4" />
                      Restaurar
                    </Button>
                  </div>
                ))}
              </Card>
            ) : null}
          </div>
        ) : null}
      </div>

      <LeadTagDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        isSubmitting={createTag.isLoading || updateTag.isLoading}
        initialData={editingTag}
      />

      <AlertDialog open={!!tagToArchive} onOpenChange={(open) => !open && setTagToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archivar etiqueta</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Archivar <strong>{tagToArchive?.name}</strong>? La etiqueta se mantiene en los leads
              que la tienen, pero no se podrá asignar de nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                tagToArchive &&
                setArchived.mutate({ merchantId, id: tagToArchive.id, archived: true })
              }
            >
              Archivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadTagsManager;
